import { useState } from "react";
import CitationButton from "./CitationButton";
import CitationModal from "./CitationModal";
import { User, FileText, File, Monitor, ChevronRight, X as XIcon } from "lucide-react";

import MarkdownMessageRenderer from "./MarkdownMessageRenderer";
import { cleanMarkdownCitations } from "../utils/cleanMarkdownCitations";
import { useAuth } from "../../auth/AuthContext";

const formatCanvasTime = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleString("th-TH", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function ChatBubble({
  m,
  hideCanvasCodeBlocks = false,
  canvasPaneOpen = false,
  onOpenCanvas = null,
  onCloseCanvas = null,
}) {
  const [showModal, setShowModal] = useState(false);
  const [imageError, setImageError] = useState(false);
  const { user } = useAuth();
  const isUser = m.role === "user";
  const hasText = Boolean((m.content || "").trim());
  const hasCanvasCode = !isUser && Boolean(m?.canvasCode);
  const attachments = Array.isArray(m.attachments) ? m.attachments : [];

  const formatAttachmentSize = (bytes) => {
    const n = Number(bytes || 0);
    if (!Number.isFinite(n) || n <= 0) return "";
    if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    if (n >= 1024) return `${Math.round(n / 1024)} KB`;
    return `${n} B`;
  };

  const fileExtLabel = (name = "", ext = "") => {
    const fromExt = String(ext || "").trim().toUpperCase();
    if (fromExt) return fromExt;
    const fromName = String(name || "").split(".").pop() || "";
    return fromName.toUpperCase() || "FILE";
  };

  // Hide transient empty assistant bubbles while streaming starts
  if (!isUser && !hasText && !hasCanvasCode && !(m.citations?.length > 0)) return null;

  return (
    <div
      className={`flex w-full gap-3 ${
        isUser ? "justify-end" : "justify-start"
      } animate-slideUp group mb-4`}
    >
      {!isUser && (
        <div className="flex-shrink-0 w-14 h-14 rounded-full border border-slate-200 bg-white/95 flex items-center justify-center shadow-sm shadow-slate-200 mt-1 overflow-hidden">
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            className="w-full h-full object-contain bg-white"
            aria-label="AI avatar"
          >
            <source src={`${process.env.PUBLIC_URL}/avatar2.mp4`} type="video/mp4" />
          </video>
        </div>
      )}

      <div
        className={`relative min-w-0 max-w-[92%] sm:max-w-[82%] md:max-w-[72%] flex flex-col ${isUser ? "items-end" : "items-start"}`}
      >
        {(hasText || isUser) && (
        <div
          className={`px-5 py-3.5 shadow-sm relative w-fit max-w-full break-words ${
            isUser
              ? "rounded-2xl rounded-tr-sm bg-gradient-to-br from-blue-600 to-cyan-600 text-white shadow-blue-500/20"
              : "rounded-2xl rounded-tl-sm bg-white border border-slate-100 text-slate-800 shadow-slate-200/50"
          }`}
        >
          <MarkdownMessageRenderer
            content={cleanMarkdownCitations(m.content)}
            isUser={isUser}
            hideCanvasCodeBlocks={hideCanvasCodeBlocks}
            canvasPaneOpen={canvasPaneOpen}
            onOpenCanvas={onOpenCanvas}
            onCloseCanvas={onCloseCanvas}
            canvasTitle={m.canvasTitle}
            canvasTimestamp={m.canvasTimestamp}
          />

          {isUser && attachments.length > 0 && (
            <div className="mt-3 pt-3 border-t border-white/25 flex flex-wrap gap-2">
              {attachments.map((att, idx) => {
                const ext = String(att?.ext || "").toLowerCase();
                const isKnownDoc = ext === "pdf" || ext === "docx";
                const Icon = isKnownDoc ? FileText : File;
                const size = formatAttachmentSize(att?.size);
                return (
                  <div
                    key={`${att?.name || "file"}-${idx}`}
                    className="min-w-0 max-w-[220px] rounded-lg bg-white/20 border border-white/30 px-2.5 py-2"
                    title={att?.name || "attachment"}
                  >
                    <div className="flex items-center gap-2">
                      <Icon size={14} className="text-white/90 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-[11px] font-semibold text-white/95 truncate">
                          {att?.name || "ไฟล์แนบ"}
                        </div>
                        <div className="text-[10px] text-white/80">
                          {fileExtLabel(att?.name, att?.ext)}{size ? ` • ${size}` : ""}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ✅ Citation button */}
          {!isUser && m.citations?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100 flex justify-start">
              <CitationButton
                citations={m.citations}
                onClick={() => setShowModal(true)}
              />
            </div>
          )}
        </div>
        )}

        {/* ✅ Gemini-style Canvas artifact card (rendered from message data, not markdown) */}
        {hasCanvasCode && (
          <div className={`w-full rounded-[20px] bg-[#f0f4f9] flex items-center justify-between p-4 gap-4 ${hasText ? "mt-2" : ""}`}>
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-10 h-10 rounded-[10px] border border-slate-300 bg-white flex items-center justify-center flex-shrink-0 text-slate-700">
                <Monitor size={18} strokeWidth={2.2} />
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-semibold text-slate-800 truncate">
                  {m.canvasTitle || "ตัวอย่าง HTML CODE"}
                </span>
                {m.canvasTimestamp ? (
                  <span className="text-xs text-slate-500 mt-0.5">
                    {formatCanvasTime(m.canvasTimestamp)}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="flex-shrink-0">
              {canvasPaneOpen ? (
                <button
                  type="button"
                  onClick={onCloseCanvas}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors"
                >
                  <XIcon size={14} />
                  ปิด
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onOpenCanvas}
                  className="inline-flex items-center gap-1 px-5 py-1.5 rounded-full text-sm font-medium bg-[#c2e7ff] text-[#001d35] hover:bg-[#b3dcf4] transition-colors"
                >
                  เปิด
                  <ChevronRight size={15} />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {isUser && (
        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center mt-1 overflow-hidden border border-slate-300/50 shadow-sm">
          {user?.image && !imageError ? (
            <img
              src={user.image}
              alt="User Avatar"
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <User size={20} className="text-slate-500" />
          )}
        </div>
      )}

      {/* ✅ Slide modal */}
      {showModal && (
        <CitationModal
          citations={m.citations}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
